export enum Accessibility {
  PUBLIC = 'PUBLIC',
  PAYING = 'PAYING',
  PRIVATE = 'PRIVATE'
}

export enum Availability {
  INOPERATIVE = 'INOPERATIVE',
  OPERATIVE = 'OPERATIVE'
}

export enum Status {
  UNKNOWN = 'UNKNOWN',
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  RESERVED = 'RESERVED',
  INOPERATIVE = 'INOPERATIVE',
  FAULTED = 'FAULTED',
  OFFLINE = 'OFFLINE'
}

export enum SubStatus {
  PREPARING = 'PREPARING',
  CHARGING = 'CHARGING',
  SUSPENDED_EVSE = 'SUSPENDED_EVSE',
  SUSPENDED_EV = 'SUSPENDED_EV',
  FINISHING = 'FINISHING'
}

export enum OcpiStatus {
  AVAILABLE = 'AVAILABLE',
  BLOCKED = 'BLOCKED',
  CHARGING = 'CHARGING',
  INOPERATIVE = 'INOPERATIVE',
  OUTOFORDER = 'OUTOFORDER',
  PLANNED = 'PLANNED',
  REMOVED = 'REMOVED',
  RESERVED = 'RESERVED',
  UNKNOWN = 'UNKNOWN'
}

export enum StopReason {
  DE_AUTHORIZED = 'DE_AUTHORIZED',
  EMERGENCY_STOP = 'EMERGENCY_STOP',
  ENERGY_LIMIT_REACHED = 'ENERGY_LIMIT_REACHED',
  EV_DISCONNECTED = 'EV_DISCONNECTED',
  GROUND_FAULT = 'GROUND_FAULT',
  IMMEDIATE_RESET = 'IMMEDIATE_RESET',
  LOCAL = 'LOCAL',
  LOCAL_OUT_OF_CREDIT = 'LOCAL_OUT_OF_CREDIT',
  MASTER_PASS = 'MASTER_PASS',
  OTHER = 'OTHER',
  OVERCURRENT_FAULT = 'OVERCURRENT_FAULT',
  POWER_LOSS = 'POWER_LOSS',
  POWER_QUALITY = 'POWER_QUALITY',
  REBOOT = 'REBOOT',
  REMOTE = 'REMOTE',
  REQ_ENERGY_TRANSFER_REJECTED = 'REQ_ENERGY_TRANSFER_REJECTED',
  SOC_LIMIT_REACHED = 'SOC_LIMIT_REACHED',
  SOFT_RESET = 'SOFT_RESET',
  STOPPED_BY_EV = 'STOPPED_BY_EV',
  TIME_LIMIT_REACHED = 'TIME_LIMIT_REACHED',
  TIMEOUT = 'TIMEOUT',
  UNLOCK_COMMAND = 'UNLOCK_COMMAND'
}

interface StatusComponent {
  status?: Status;
  subStatus?: SubStatus;
}

export enum AuthenticationStatus {
  ACCEPTED = 'ACCEPTED',
  BLOCKED = 'BLOCKED',
  EXPIRED = 'EXPIRED',
  INVALID = 'INVALID',
  CONCURRENT = 'CONCURRENT_TX',
  DELETED = 'DELETED'
}

export interface LocalAuthorization {
  token: string;
  authenticationStatus: AuthenticationStatus;
}

export enum DayOfWeek {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday'
}

export interface TimeOfDay {
  hourOfDay: number;
  minutesInHour: number;
}

export interface OpeningTime {
  day: DayOfWeek;
  timeStart: TimeOfDay;
  timeStop: TimeOfDay;
}

export enum EvseStatus {
  REMOVED = 'REMOVED',
  ACTIVE = 'ACTIVE'
}

export enum Current {
  UNSPECIFIED = 'UNSPECIFIED',
  AC = 'AC',
  DC = 'DC'
}

export enum ConnectorType {
  UNSPECIFIED = 'UNSPECIFIED',
  C_TYPE_1 = 'C_TYPE_1',
  C_TYPE_2 = 'C_TYPE_2',
  S_TYPE_2 = 'S_TYPE_2',
  S_TYPE_3 = 'S_TYPE_3',
  C_CCS_1 = 'C_CCS_1',
  C_CCS_2 = 'C_CCS_2',
  C_G105 = 'C_G105',
  CEE_7_7 = 'CEE_7_7',
  S_BS1361 = 'S_BS1361',
  S_309_1P_16A = 'S_309_1P_16A',
  S_309_3P_16A = 'S_309_3P_16A',
  S_309_3P_32A = 'S_309_3P_32A'
}

export interface Connector extends StatusComponent {
  connectorId: number;
  maxAmp: number;
  phase: number;
  voltage: number;
  maxElectricPower: number;
  current: 'AC' | 'DC' | 'UNSPECIFIED';
  connectorType: ConnectorType;
  physicalReference?: string | null;
  errorCode?: string;
  errorInfo?: string;
  vendorErrorCode?: string;
}

export enum Capability {
  CHARGING_PROFILE_CAPABLE = 'CHARGING_PROFILE_CAPABLE',
  CHARGING_PREFERENCES_CAPABLE = 'CHARGING_PREFERENCES_CAPABLE',
  CHIP_CARD_SUPPORT = 'CHIP_CARD_SUPPORT',
  CONTACTLESS_CARD_SUPPORT = 'CONTACTLESS_CARD_SUPPORT',
  CREDIT_CARD_PAYABLE = 'CREDIT_CARD_PAYABLE',
  DEBIT_CARD_PAYABLE = 'DEBIT_CARD_PAYABLE',
  PED_TERMINAL = 'PED_TERMINAL',
  REMOTE_START_STOP_CAPABLE = 'REMOTE_START_STOP_CAPABLE',
  RESERVABLE = 'RESERVABLE',
  RFID_READER = 'RFID_READER',
  START_SESSION_CONNECTOR_REQUIRED = 'START_SESSION_CONNECTOR_REQUIRED',
  TOKEN_GROUP_CAPABLE = 'TOKEN_GROUP_CAPABLE',
  UNLOCK_CAPABLE = 'UNLOCK_CAPABLE'
}

export enum ParkingRestriction {
  CUSTOMERS = 'CUSTOMERS',
  DISABLED = 'DISABLED',
  EV_ONLY = 'EV_ONLY',
  MOTORCYCLES = 'MOTORCYCLES',
  PLUGGED = 'PLUGGED'
}

export interface Evse extends StatusComponent {
  id: number;
  ocppEvseId: number;
  evseId: string;
  floorLevel?: string;
  physicalReference?: string;
  parkingRestrictions?: ParkingRestriction[];
  capabilities?: Capability[];
  connectors: Connector[];
  availability: Availability;
  evseStatus: EvseStatus;
  statusOverride?: OcpiStatus;
}

export enum SecurityProfile {
  PROFILE_0 = 'PROFILE_0',
  PROFILE_1 = 'PROFILE_1',
  PROFILE_2 = 'PROFILE_2',
  PROFILE_3 = 'PROFILE_3'
}

export enum PasswordStatus {
  CLEARED = 'CLEARED',
  PASSWORD_SET = 'PASSWORD_SET',
  SET_PASSWORD_FAILED = 'SET_PASSWORD_FAILED',
  SETTING_PASSWORD = 'SETTING_PASSWORD'
}

export enum WebSocketAuthMode {
  DEFAULT = 'DEFAULT',
  NONE = 'NONE',
  NOT_MANDATORY = 'NOT_MANDATORY'
}

export interface SecuritySettings {
  securedConnection: boolean;
  ipAddress?: string;
  securityProfile?: SecurityProfile;
  passwordStatus?: PasswordStatus;
  passwordChangedAt?: Date;
  preferredProtocol?: string;
  authorizationMode?: WebSocketAuthMode;
}

export interface ChargingStation extends StatusComponent {
  id: string;
  protocol: string;
  accepted: boolean;
  reservable: boolean;
  configured: boolean;
  blocked: boolean;
  latitude?: number;
  longitude?: number;
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  locationId?: string;
  locationName?: string;
  city?: string;
  region?: string;
  country?: string;
  accessibility?: Accessibility;
  availability?: Availability;
  localAuthorizationListVersion: number;
  localAuths: LocalAuthorization[];
  openingTimes: OpeningTime[];
  attributes: Record<string, string>;
  confItems: Record<string, string>;
  firstSeen?: Date;
  lastSeen?: Date;
  evses: Evse[];
  customIdentifier?: string;
  securitySettings: SecuritySettings;
  aggregateStatus?: Status;
  aggregateSubStatus?: SubStatus;
  errorCode?: string;
  errorInfo?: string;
  vendorErrorCode?: string;
  lastStatusUpdate?: Date;
  pools?: { id: string; name: string }[];
}

export interface ChargingStationStatusOverview {
  availableCount: number;
  preparingCount: number;
  chargingCount: number;
  suspendedEVCount: number;
  suspendedEVSECount: number;
  finishingCount: number;
  reservedCount: number;
  unavailableCount: number;
  faultedCount: number;
  offlineCount: number;
  unknownCount: number;
  totalCount: number;
}

export interface EvseId {
  id: string;
  numberedId: number;
}

export interface MeterValue {
  timestamp: string;
  wattHour: string;
}

export interface EnergyConsumption {
  wattHour: number;
  timestamp: Date;
}

export interface Transaction {
  id: number;
  updated: Date;
  created: Date;
  chargingStationId: string;
  transactionId: string;
  idTag: string;
  meterStart: number;
  meterStop: number;
  evseId: EvseId;
  poolId: string;
  startedTimestamp: Date;
  stoppedTimestamp: Date;
  stopReason: StopReason;
  forceStopped?: boolean;
  meterValues?: MeterValue[];
  energyConsumption: EnergyConsumption;
  authorizationReportId?: number;
  authProvider?: string;
  ocpiSessionId?: string;
  ocpiCdrId?: string;
}

export interface PoolChargingStation {
  id: string;
  status?: Status;
  subStatus?: SubStatus;
  aggregateStatus?: Status;
  aggregateSubStatus?: SubStatus;
  evses?: [
    {
      status: Status;
    }
  ];
}

export interface CreateChargingStationCommand {
  id: string;
  model: string;
  vendor: string;
}
